<script setup lang="ts">
import type { Campaign } from "~/types/campaign";
import { stripHtml, truncateForMeta } from "@donoor/utils/string";
import { campaignsPublicApi } from "~/services/api/campaignsPublic";
import { LOCALE_INTL, type Langue } from "~/utils/langue";
import { useChatPageContext, euros, dateLisible, phrasePaysAssociation } from "~/composables/useChatPageContext";
import { navLinks } from "~/utils/navLinks";
import { modeleCagnotteDemande } from "~/utils/modelePageCagnotte";
import { useDemoAccueil, useDonneeDemoAccueil } from "~/composables/useDemoAccueil";
import PageCagnotteModele from "~/components/page-cagnotte/PageCagnotteModele.vue";
import PageCagnotteAncienModele from "~/components/page-cagnotte/ancien/PageCagnotteAncienModele.vue";

/**
 * Page publique d'une cagnotte. Ce fichier porte ce qui est COMMUN à toutes
 * les mises en page : chargement (SSR), référencement, aperçu du dashboard,
 * états d'erreur, statistiques de visite, contexte du chat. La mise en page
 * elle-même est choisie par UN réglage (`MODELE_PAGE_CAGNOTTE`,
 * utils/modelePageCagnotte.ts) : « immersif » (retenu le 2026-10-13),
 * « mosaique », « banniere » (PageCagnotteModele) ou « ancien »
 * (PageCagnotteAncienModele, la page d'avant).
 */
const route = useRoute();
// Démo de la home (sans iframe) : le slug vient de la démo, et la page ne
// touche à rien de global (titre, Google, 404, visite, header, pied de page).
const demoAccueil = useDemoAccueil();
const slug = demoAccueil.integree?.slug ?? (route.params as { slug: string }).slug;
const config = useRuntimeConfig();
const { t, locale } = useI18n();
const ogLocale = (LOCALE_INTL[locale.value as Langue] ?? "fr-FR").replace("-", "_");

// Mode APERÇU du dashboard (?preview=true) : la page est cadrée en iframe dans
// la section « Aperçu » de la gestion de cagnotte. Trois effets — l'API
// court-circuite son cache de 5 min, le brouillon en cours d'édition est poussé
// en direct par postMessage, et la visite n'est pas comptée.
// Parité adhésion / billetterie / boutique. Karim 2026-08-30.
const isPreview = computed(() => route.query.preview === "true");
const apiUrl = config.public.apiBaseUrl;
const assetsBaseUrl = config.public.apiUrl;

// ============================================================
// SSR: Fetch campaign data server-side for SEO + initial render
// ============================================================
// `nf` (not found) : l'API a répondu explicitement 404 → la cagnotte n'existe
// pas/plus. On distingue du null générique (panne API/timeout) : envoyer un
// statut 404 sur une erreur transitoire ferait désindexer des cagnottes
// VIVANTES par Google. 404 UNIQUEMENT sur 404 API confirmé (audit SEO 2026-08-01).
// La clé PORTE le mode aperçu : sans lui, la charge SSR mise en cache pour la
// page publique serait réutilisée dans l'iframe d'aperçu, et l'édition en
// cours resterait invisible. Démo de la home : clé à part, rendu du serveur
// repris (cf. useDonneeDemoAccueil).
const donneeCagnotte = useDonneeDemoAccueil(`campaign-ssr-${slug}${isPreview.value ? "-preview" : ""}`);
const { data: ssrResult } = await useAsyncData(
  donneeCagnotte.key,
  async (): Promise<{ c: Campaign | null; nf: boolean }> => {
    try {
      const res = await $fetch<{ data: Campaign }>(
        `${apiUrl}/api/campaigns/public/slug/${slug}${isPreview.value ? "?preview=true" : ""}`,
      );
      return { c: res.data || null, nf: !res.data };
    } catch (e: unknown) {
      const status =
        (e as { statusCode?: number; status?: number })?.statusCode ??
        (e as { status?: number })?.status;
      return { c: null, nf: status === 404 };
    }
  },
  { getCachedData: donneeCagnotte.getCachedData, server: true, lazy: false },
);
const ssrCampaign = computed(() => ssrResult.value?.c ?? null);

// Vrai statut HTTP 404 côté SSR quand la cagnotte n'existe pas : Google reçoit
// le bon signal (avant : 200 + noindex = « soft 404 », crawl budget gaspillé).
// L'HUMAIN, lui, voit la page « introuvable » + suggestions (cf. template).
if (import.meta.server && ssrResult.value?.nf && !demoAccueil.estIntegree) {
  const event = useRequestEvent();
  if (event) setResponseStatus(event, 404);
}

// stripHtml importé depuis @donoor/utils/string (source unique cross-app).

// Origine du site COURANT (prod/staging/préview) pour les og:image
// auto-référentes — cf. useShareBaseUrl. Canonical reste prod en dur.
const shareBase = useShareBaseUrl();

const getAbsoluteImageUrl = (imageId: string | null | undefined): string => {
  if (!imageId) return `${shareBase}/og-image.png`;
  if (imageId.startsWith("http")) return imageId;
  return `${assetsBaseUrl}/api/assets/${imageId}?width=1200&height=630&fit=cover&format=jpg&quality=80`;
};

const _ssr = ssrCampaign.value;
// OG image dynamique (sprint v4 Karim 2026-06-15) : route /api/og/campaign
// qui génère un PNG violet 1200×630 avec titre + progress bar + montant raised
// + asso + nombre de donateurs. CTR partage social ~30% supérieur à la simple
// cover_image. Fallback cover_image si le slug est inconnu.
const ogImageUrl = slug
  ? `${shareBase}/api/og/campaign?slug=${slug}`
  : getAbsoluteImageUrl(_ssr?.image);
const cleanDescription = stripHtml(_ssr?.description);
// Meta description SEO : on préfixe « Cagnotte {asso} : » pour renforcer le
// match sur « cagnotte {asso} » (Karim 2026-08-04), puis le titre + un extrait
// de la description saisie par l'asso.
const orgNameForDesc = _ssr?.organization_name?.trim();
const descPrefix = orgNameForDesc
  ? t("campagne.pageDetail.prefixCagnotteAsso", { asso: orgNameForDesc })
  : t("campagne.pageDetail.prefixCagnotte");
const ogDescription = cleanDescription
  ? truncateForMeta(`${descPrefix}${_ssr?.title} — ${cleanDescription}`, 160)
  : _ssr?.title
    ? t("campagne.pageDetail.ogDescriptionTitreSeul", { prefix: descPrefix, titre: _ssr.title })
    : t("campagne.pageDetail.ogDescriptionParDefaut");
const ogTitle = _ssr?.title || t("campagne.pageDetail.ogTitreParDefaut");
const canonicalUrl = `https://donoor.org/c/${slug}`;

// Titre enrichi SEO : « Cagnotte {Nom asso} — {Titre} » (Karim 2026-08-04).
// AVANT : « {Titre} — {Nom asso} » plaçait le mot « cagnotte » nulle part et le
// nom de l'asso EN FIN → une cagnotte comme « festival Les Aiguilleurs — SUM »
// ne ressortait pas sur « cagnotte SUM » ni « SUM association » (Google la
// voyait comme « festival Les Aiguilleurs »). On met désormais en TÊTE les deux
// signaux que les gens tapent : le mot « Cagnotte » + le nom de l'asso, PUIS le
// titre saisi par l'asso (jamais réécrit). Matche « cagnotte {asso} »,
// « {asso} cagnotte », « {asso} don » ET « {titre} ».
const orgNameForTitle = _ssr?.organization_name?.trim();
const seoTitle = _ssr?.title
  ? orgNameForTitle
    ? _ssr.title.toLowerCase().includes(orgNameForTitle.toLowerCase())
      ? // Le nom de l'asso est DÉJÀ dans le titre : on préfixe juste « Cagnotte »
        t("campagne.pageDetail.seoTitreSansAsso", { titre: _ssr.title })
      : t("campagne.pageDetail.seoTitreAvecAsso", { asso: orgNameForTitle, titre: _ssr.title })
    : t("campagne.pageDetail.seoTitreSansAsso", { titre: _ssr.title })
  : t("campagne.pageDetail.seoTitreParDefaut");

// Fil d'Ariane visible (SeoBreadcrumb émet aussi le JSON-LD BreadcrumbList).
// Niveau asso → /associations/{slug} : chaque cagnotte lie sa page asso.
const breadcrumbItems = [
  { name: t("campagne.pageDetail.breadcrumbCagnottes"), path: "/recherche/cagnottes" },
  ...(_ssr?.organization_name && _ssr?.organization_slug
    ? [
        {
          name: _ssr.organization_name,
          path: `/associations/${_ssr.organization_slug}`,
        },
      ]
    : []),
  { name: _ssr?.title || t("campagne.pageDetail.seoTitreParDefaut") },
];

// Noindex draft/paused/canceled campaigns and 404s — they shouldn't pollute the
// index. Active and completed campaigns stay indexable (completed ones still have
// valuable SEO content: title, story, total raised, etc.).
const shouldNoindex =
  !_ssr ||
  // Cagnotte PRIVÉE : accessible par son lien, jamais indexée — même règle
  // que l'événement, la boutique et l'adhésion privés (Karim 2026-09-23).
  (_ssr as { is_public?: boolean | null }).is_public === false ||
  _ssr.status === "draft" ||
  _ssr.status === "paused" ||
  _ssr.status === "canceled";
// robots cohérent avec l'indexabilité globale (staging = noindex) + noindex
// page si la cagnotte n'est pas dans un statut indexable.
const robotsContent = useRobotsContent(() => shouldNoindex);

if (!demoAccueil.estIntegree) {
useSeoMeta({
  title: seoTitle,
  description: ogDescription,
  robots: () => robotsContent.value,
  ogType: "website",
  ogTitle: ogTitle,
  ogDescription: ogDescription,
  ogImage: ogImageUrl,
  ogImageWidth: 1200,
  ogImageHeight: 630,
  ogImageAlt: t("campagne.pageDetail.ogImageAlt", { titre: ogTitle }),
  ogUrl: canonicalUrl,
  ogSiteName: "Donoor",
  ogLocale,
  twitterCard: "summary_large_image",
  twitterTitle: ogTitle,
  twitterDescription: ogDescription,
  twitterImage: ogImageUrl,
  twitterSite: "@donoor_org",
});

useHead({
  link: [{ rel: "canonical", key: "canonical", href: canonicalUrl }],
  script: [
    // DonateAction schema
    ...(_ssr
      ? [
          {
            type: "application/ld+json" as const,
            innerHTML: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "DonateAction",
              name: _ssr.title,
              description: ogDescription,
              url: canonicalUrl,
              image: ogImageUrl,
              recipient: {
                "@type": "Organization",
                name: _ssr.organization_name || "Donoor",
                // Rattache le bénéficiaire à SA fiche (même @id que le schéma
                // de la page association) au lieu d'une entité anonyme.
                ...(_ssr.organization_slug && {
                  "@id": `https://donoor.org/associations/${_ssr.organization_slug}#organization`,
                  url: `https://donoor.org/associations/${_ssr.organization_slug}`,
                }),
                ...(_ssr.organization_logo && {
                  logo: `${assetsBaseUrl}/api/assets/${_ssr.organization_logo}`,
                }),
              },
              // Où donner — toujours, objectif ou pas. Le montant collecté
              // n'est PAS un prix : `priceSpecification` le déclarait comme tel
              // (audit SEO 2026-10-18), il reste dans le texte visible.
              target: {
                "@type": "EntryPoint",
                urlTemplate: `${canonicalUrl}/payment`,
              },
            }),
          },
        ]
      : []),
    // BreadcrumbList JSON-LD : émis par <SeoBreadcrumb> (fil d'Ariane visible
    // du template) — plus de schema manuel ici pour éviter le doublon.
    // Rappel : /cagnottes est un 301 → toujours /cagnottes dans le
    // breadcrumb (audit SEO 2026-07-11).
    // WebPage schema
    ...(_ssr
      ? [
          {
            type: "application/ld+json" as const,
            innerHTML: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebPage",
              name: _ssr.title,
              description: ogDescription,
              url: canonicalUrl,
              isPartOf: {
                "@type": "WebSite",
                name: "Donoor",
                url: "https://donoor.org",
              },
              datePublished: _ssr.date_created || undefined,
              inLanguage: (LOCALE_INTL[locale.value as Langue] ?? "fr-FR"),
            }),
          },
        ]
      : []),
  ],
});
}

// ============================================================
// Client: Full composable for interactivity (after hydration)
// Pass SSR data to avoid double-fetch and flash of unstyled content
// ============================================================
const controleur = useCampaignPublic(slug, ssrCampaign.value);
const {
  campaign: campaignSource,
  applyIncomingDonation,
  isLoading,
  error,
  errorType,
  loadCampaign,
} = controleur;

// Démo de la home : la cagnotte, rendue avec la home au déploiement, est relue
// au démarrage de la démo (cf. useDonneeDemoAccueil) → montant collecté et
// donateurs du moment.
if (demoAccueil.estIntegree) {
  watch(ssrCampaign, (fraiche) => {
    if (fraiche) campaignSource.value = fraiche;
  });
}

/** La mise en page affichée (réglage unique ; `?modele=` en développement). */
const modele = modeleCagnotteDemande(route.query);

// APERÇU TEMPS RÉEL — le dashboard pousse le brouillon en cours d'édition par
// postMessage ; il se superpose aux données chargées. Inerte hors
// `?preview=true`.
//
// La surcouche est posée ICI, en sortie du contrôleur, plutôt que dans son
// `ref` interne : celui-ci est réécrit par les rechargements et le socket
// temps réel, qui effaceraient le brouillon à chaque don reçu.
const draftPreview = useEntityDraftPreview({
  isPreviewMode: isPreview,
  // Don SIMULÉ depuis l'aperçu du dashboard : on emprunte le chemin RÉEL d'un
  // don entrant (montant, progression, liste, meilleur donateur, notification),
  // pour que la démonstration soit fidèle.
  onSimulate: (payload) => {
    applyIncomingDonation(payload as never);
  },
});
const campaign = computed(() => draftPreview.mergeEntity(campaignSource.value));

// Copy de l'état d'erreur (privée / introuvable / générique) pour
// PublicPageErrorState — même idiome que /a/[slug] et /b/[slug]. L'illustration
// maison « mains stop » (hands-no) est plus chaleureuse que l'icône générique
// pour le cas « introuvable/supprimée ». Karim 2026-07-09.
const errorCopy = computed(() => {
  switch (errorType.value) {
    case "private":
      return {
        icon: "i-lucide-lock",
        title: t("campagne.pageDetail.cagnottePriveeTitre"),
        message: error.value ?? t("campagne.controllerPublic.erreurPrivee"),
      };
    case "not_found":
      return {
        icon: "/svg/hands-no.svg",
        title: t("campagne.pageDetail.cagnotteIntrouvableTitre"),
        message: error.value ?? t("campagne.controllerPublic.erreurIntrouvable"),
      };
    default:
      return {
        icon: "i-lucide-alert-circle",
        title: t("don.pageErreur.titreErreurGenerique"),
        message: error.value ?? t("campagne.pageDetail.erreurChargementCagnotteMessage"),
      };
  }
});

// Contexte donné à l'IA du chat : elle aide en priorité sur CETTE cagnotte
// tant que le visiteur ne change pas de sujet (Karim 2026-10-12).
const { definirContexte, effacerContexte } = useChatPageContext();
watchEffect(() => {
  const c = campaign.value;
  if (!c) return;
  const cloture = dateLisible(c.end_date);
  const phrases = [
    `Cagnotte « ${c.title} »${c.organization_name ? ` de l'association ${c.organization_name}` : ""}.`,
    `Objectif ${euros(c.goal_amount)}, ${euros(c.current_amount)} déjà collectés${c.percent != null ? ` (${Math.round(c.percent)} %)` : ""}${c.donors_count != null ? `, ${c.donors_count} donateur(s)` : ""}.`,
    cloture ? `Clôture le ${cloture}.` : "Pas de date de clôture (cagnotte ouverte en continu).",
    c.description ? `Description : ${stripHtml(c.description).slice(0, 600)}` : null,
    phrasePaysAssociation(c.country),
  ].filter(Boolean);
  definirContexte("cagnotte", phrases.join(" "));
});
onUnmounted(() => effacerContexte());

onMounted(() => {
  loadCampaign();

  // Track view (fire-and-forget, analytics best-effort).
  // JAMAIS en aperçu : l'organisation qui relit sa page ne doit pas gonfler
  // ses propres statistiques de visite.
  // Ni dans la démo de la page d'accueil (?demo=1) : ces visiteurs ne sont
  // pas ceux de l'association.
  if (!isPreview.value && !demoAccueil.estIntegree) void campaignsPublicApi.trackView(slug);
});
</script>

<template>
  <div class="min-h-screen flex flex-col bg-gray-50">
    <!-- Surveillance : asso suspended/banned → page indisponibilité -->
    <template
      v-if="
        campaign &&
        (campaign.organization_risk_status === 'suspended' ||
          campaign.organization_risk_status === 'banned')
      "
    >
      <AppHeader :links="navLinks" rayon="cagnottes" />
      <OrgUnavailable
        :risk-status="campaign.organization_risk_status"
        :reason="campaign.organization_risk_reason"
        :organization-name="campaign.organization_name ?? null"
      />
      <!-- Suggestions + footer : la page ne doit pas être un cul-de-sac,
           même raison que la branche 404 (Karim 2026-08-01). -->
      <div class="max-w-6xl mx-auto px-4 pb-16">
        <NotFoundSuggestions
            alternative type="cagnottes" />
      </div>
      <FooterLite class="mt-auto" />
    </template>

    <!-- ============================================================ -->
    <!-- LOADING (skeleton only when no data yet — SSR provides data) -->
    <!-- ============================================================ -->
    <CampaignPageSkeleton v-else-if="isLoading && !campaign" />

    <!-- ============================================================ -->
    <!-- ERROR STATE — composant partagé (idem /a/[slug], /b/[slug]) : -->
    <!-- carte centrée + illustration maison « mains » pour introuvable, -->
    <!-- CTA « Découvrir les associations » + « Retour à l'accueil ».   -->
    <!-- ============================================================ -->
    <PublicPageErrorState
      v-else-if="error"
      rayon="cagnottes"
      :icon="errorCopy.icon"
      :title="errorCopy.title"
      :message="errorCopy.message"
    >
      <!-- Cagnotte introuvable/supprimée → proposer des cagnottes ACTIVES
           (Karim 2026-08-01 : la page 404 ne doit pas être un cul-de-sac). -->
      <template v-if="errorType === 'not_found'" #suggestions>
        <NotFoundSuggestions type="cagnottes" />
      </template>
    </PublicPageErrorState>

    <!-- ============================================================ -->
    <!-- CAMPAIGN CONTENT (full interactive, after client hydration)    -->
    <!-- ============================================================ -->
    <div v-if="!error && campaign" class="animate-fade-in grow flex flex-col">
      <!-- Fil d'Ariane (JSON-LD BreadcrumbList pour Google ; invisible) -->
      <SeoBreadcrumb v-if="!demoAccueil.estIntegree" :items="breadcrumbItems" :hide-visual="true" />

      <PageCagnotteAncienModele v-if="modele === 'ancien'" :campagne="campaign" :controleur="controleur" />
      <PageCagnotteModele v-else :campagne="campaign" :controleur="controleur" :modele="modele" />

      <!-- Section « Découvrez d'autres cagnottes » MASQUÉE (Karim 2026-08-06,
           pattern HelloAsso) : chaque page se concentre sur SA cagnotte.
      <CampaignRelatedSection :slug="slug" />
      -->

      <!-- Footer LÉGER (pattern HelloAsso) : liens légaux + CTA compact,
           zéro porte de sortie produit sur une page de collecte. -->
      <FooterLite v-if="!demoAccueil.estIntegree" class="mt-auto" />
    </div>
  </div>
</template>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.4s ease-out;
}
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
